import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Response, SummaryRequest } from '../../../_shared/models';
import { KpiStatsResponse } from '../../../_shared/models/kpi-response.model';
import { Pagination } from '../../../_shared/models/pagination-response.model';
import { delay } from '../../../_shared/helpers/delay.helper';
import { AccountDateFilterRequest } from '../../../_shared/models/account-date-filter-request.model';
import { AssetCostTableRequest, BudgetCategory, BudgetTableRequest, CreateBudgetsRequest, UpdateBudgetRequest } from '../models/cost-tracking-request.model';
import {
  CostSummary,
  CostDistributionSummary,
  CostCategoryDistribution,
  AssetCostItem,
  MaintenanceInsightsResponse,
  MaintenanceKpiResponse,
  BudgetVsActualInsightsResponse,
  BudgetItem
} from '../models/cost-tracking-response.model';
import { CostCategory } from '../mockdata/cost-tracking.mock';
import {
  buildActualInsights,
  buildAnnualBudgetSummary,
  buildBudgetedInsights,
  buildBudgetVsActualSummary,
  buildCategoryCostSummary,
  buildCostDistributionSummary,
  buildMaintenanceBenchmark,
  buildMaintenanceInsights,
  buildMaintenanceSummary,
} from '../mockdata/cost-tracking-mock.helpers';

@Injectable({
  providedIn: 'root'
})
export class CostTrackingService {
  private readonly httpClient = inject(HttpClient);
  private readonly costTrackingUrl = `${environment.apiEndpoint}/costtracking`;
  readonly budgetsChanged$: Subject<void> = new Subject<void>();

  async getTotalRepairCosts(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient
        .post<Response<KpiStatsResponse>>(`${environment.apiEndpoint}/costtrackinginsights/total-repair-cost`, request)
        .pipe(takeUntil(cancellation))
    );
  }

  async getTotalInsuranceCost(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<CostSummary>> {
    await delay(800);
    return {
      data: buildCategoryCostSummary(request, CostCategory.Insurance),
    };
  }

  async getTotalComplianceCosts(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<CostSummary>> {
    await delay(800);
    return {
      data: buildCategoryCostSummary(request, CostCategory.Compliance),
    };
  }

  async getAnnualBudget(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<CostSummary>> {
    await delay(800);
    return {
      data: buildAnnualBudgetSummary(request),
    };
  }

  async getCostDistributionSummary(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<CostDistributionSummary>> {
    await delay(1000);
    return {
      data: buildCostDistributionSummary(request),
    };
  }

  async getMaintenanceYouInsights(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<MaintenanceInsightsResponse[]>> {
    await delay(2000);

    return {
      data: buildMaintenanceInsights(request),
    };
  }

  async getMaintenanceBenchmarkValue(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<number>> {
    await delay(500);

    return {
      data: buildMaintenanceBenchmark(request),
    };
  }

  async getMaintenanceSummary(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<MaintenanceKpiResponse>> {
    await delay(2000);

    return {
      data: buildMaintenanceSummary(request),
    };
  }

  async getBudgetedInsights(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<BudgetVsActualInsightsResponse[]>> {
    await delay(2000);

    return {
      data: buildBudgetedInsights(request),
    };
  }

  async getActualInsights(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<BudgetVsActualInsightsResponse[]>> {
    await delay(2000);

    return {
      data: buildActualInsights(request),
    };
  }

  async getBudgetVsActualSummary(
    request: AccountDateFilterRequest,
    cancelSubject: Subject<void>
  ): Promise<Response<MaintenanceKpiResponse>> {
    await delay(2000);

    return {
      data: buildBudgetVsActualSummary(request),
    };
  }

  async getCostCategoryDistribution(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<CostCategoryDistribution>> {
    return firstValueFrom(
      this.httpClient.post<Response<CostCategoryDistribution>>(
        `${this.costTrackingUrl}/cost-distribution/categories`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  async getAssetCostTable(request: AssetCostTableRequest, cancellation: Subject<void>): Promise<Response<Pagination<AssetCostItem[]>>> {
    return firstValueFrom(
      this.httpClient.post<Response<Pagination<AssetCostItem[]>>>(
        `${this.costTrackingUrl}/asset-costs/filter`,
        request
      ).pipe(takeUntil(cancellation))
    );
  }

  private budgetCategoriesRequest: Promise<Response<BudgetCategory[]>> | null = null;

  getBudgetCategories(): Promise<Response<BudgetCategory[]>> {
    this.budgetCategoriesRequest ??= firstValueFrom(
      this.httpClient.get<Response<BudgetCategory[]>>(`${this.costTrackingUrl}/budget-categories`)
    ).catch((error) => {
      this.budgetCategoriesRequest = null; // let the next caller retry after a failure
      throw error;
    });

    return this.budgetCategoriesRequest;
  }

  getBudgetTable(request: BudgetTableRequest, cancellation: Subject<void>): Promise<Response<Pagination<BudgetItem[]>>> {
    return firstValueFrom(
      this.httpClient.post<Response<Pagination<BudgetItem[]>>>(`${this.costTrackingUrl}/budgets/filter`, request).pipe(takeUntil(cancellation))
    );
  }

  async createBudgets(request: CreateBudgetsRequest): Promise<Response<void>> {
    const response = await firstValueFrom(this.httpClient.post<Response<void>>(`${this.costTrackingUrl}/budgets`, request));
    this.budgetsChanged$.next();
    return response;
  }

  async updateBudget(id: number, request: UpdateBudgetRequest): Promise<Response<void>> {
    const response = await firstValueFrom(this.httpClient.put<Response<void>>(`${this.costTrackingUrl}/budgets/${id}`, request));
    this.budgetsChanged$.next();
    return response;
  }

  async deleteBudget(id: number): Promise<Response<void>> {
    const response = await firstValueFrom(this.httpClient.delete<Response<void>>(`${this.costTrackingUrl}/budgets/${id}`));
    this.budgetsChanged$.next();
    return response;
  }
}